import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { Comparable } from '../models/comparable.model';
import { DrillDownActions } from '../study-slide-outs/sections/company-drill-down/drill-down-action-types';
import _ from 'lodash-es';
import { CompanySearchActions } from '../study-slide-outs/sections/company-search/company-search-action-types';
import { CompanyInfo } from '../models/company-info.model';
import { MasterMatrixActions } from '../analysis/master-matrix/master-matrix-action-types';
import { InternetReviewActions } from '../analysis/internetreview/internet-review-action-types';
import { FinancialReviewActions } from '../analysis/financialadjustments/financial-review-action-types';
import { StudyActions } from '../study-action-types';

export interface ComparablesState {
  details: ComparableDetailsState;
}

export type ComparableDetailsState = EntityState<Comparable>;

export const adapter = createEntityAdapter<Comparable>({
  selectId: (comparable) => comparable.comparableId,
});

export const initialComparablesState: ComparablesState = {
  details: adapter.getInitialState(),
};

export const comparablesReducer = createReducer(
  initialComparablesState,

  on(CompanySearchActions.companiesLoaded, CompanySearchActions.pageDataForDrillDownLoaded, (state, action) => {
    return {
      ...state,
      details: adapter.upsertMany(
        action.companies.map((c) => {
          const comparable = addOrUpdateComparable(c, state);
          comparable.studyComparable = !!comparable.studyComparable;
          comparable.databases = c.databases;
          comparable.companyInfo[c.databaseId].countryName = c.countryName;

          return comparable;
        }),
        state.details
      ),
    };
  }),

  on(
    MasterMatrixActions.pageDataLoaded,
    MasterMatrixActions.pageDataForDrillDownLoaded,
    InternetReviewActions.pageDataLoaded,
    InternetReviewActions.comparableDataLoaded,
    FinancialReviewActions.pageDataLoaded,
    FinancialReviewActions.comparableDataLoaded,
    (state, action) => {
      return {
        ...state,
        details: adapter.upsertMany(
          action.comparables.map((c) => {
            const comparable = addOrUpdateComparable(c, state);
            comparable.studyComparable = true;

            return comparable;
          }),
          state.details
        ),
      };
    }
  ),

  on(DrillDownActions.companyDetailsLoaded, (state, action) => {
    return {
      ...state,
      details: adapter.upsertOne(
        {
          ...state.details.entities[action.id],
          comparableId: action.id,
          groupLinkId: action.detils.groupLinkId,
          databases: action.detils.databases,
          hasDrillDownDetailsLoaded: true,
          companyInfo: {
            [action.databaseId]: {
              ...state.details.entities[action.id].companyInfo[action.databaseId],
              address: action.detils.companyInfoWithBusinessDescription.address,
              cik: action.detils.companyInfoWithBusinessDescription.cik,
              financialType: action.detils.companyInfoWithBusinessDescription.financialType,
              idNumber: action.detils.companyInfoWithBusinessDescription.idNumber,
              independenceIndicator: action.detils.companyInfoWithBusinessDescription.independenceIndicator,
              phone: action.detils.companyInfoWithBusinessDescription.phone,
              segmentedFinancials: action.detils.companyInfoWithBusinessDescription.segmentedFinancials,
              status: action.detils.companyInfoWithBusinessDescription.status,
              stockExchange: action.detils.companyInfoWithBusinessDescription.stockExchange,
              tickerSymbol: action.detils.companyInfoWithBusinessDescription.tickerSymbol,
              type: action.detils.companyInfoWithBusinessDescription.type,
              webSite: action.detils.companyInfoWithBusinessDescription.webSite,
              yearIncorporated: action.detils.companyInfoWithBusinessDescription.yearIncorporated,
            },
          },
          industryDescriptions: {
            [action.databaseId]: action.detils.industryDescriptions,
          },
          businessDescriptions: {
            [action.databaseId]: {
              fullOverview: action.detils.companyInfoWithBusinessDescription.fullOverview,
              tradeDescription: action.detils.companyInfoWithBusinessDescription.tradeDescription,
              otherLanguage: action.detils.companyInfoWithBusinessDescription.otherLanguage,
            },
          },
          subsidiaries: action.detils.subsidiaries,
          shareHolders: action.detils.shareHolders,
          financialData: {
            categoryNames: [],
            items: [],
            hasSingleTypeOfFinancials: !action.detils.typeOfFinancialsData ? undefined : action.detils.typeOfFinancialsData.hasSingleTypeOfFinancials,
            isConsolidated: !action.detils.typeOfFinancialsData ? false : action.detils.typeOfFinancialsData.isConsolidated,
            currencyData: action.detils.currencyData,
            alignmentMethodId: action.detils.financialData.alignmentMethodId,
            monthsBack: action.detils.financialData.monthsBack,
            testedPartyFiscalYearEnd: action.detils.financialData.testedPartyFiscalYearEnd,
            financialsRange: action.detils.financialData.financialsRange,
          },
        },
        state.details
      ),
    };
  }),

  on(DrillDownActions.financialInitialLoaded, (state, action) => {
    return {
      ...state,
      details: adapter.updateOne(
        {
          id: action.id,
          changes: {
            ...state.details[action.id],
            financialData: {
              ...state.details.entities[action.id].financialData,
              categoryNames: _.keys(action.data.financials),
              items: {
                [action.databaseId + action.currencyId + +action.consolidated]: action.data,
              },
            },
          },
        },
        state.details
      ),
    };
  }),

  on(DrillDownActions.financialLoaded, (state, action) => {
    return {
      ...state,
      details: adapter.updateOne(
        {
          id: action.id,
          changes: {
            ...state.details[action.id],
            financialData: {
              ...state.details.entities[action.id].financialData,
              categoryNames:
                state.details.entities[action.id].financialData.categoryNames.length > 0
                  ? state.details.entities[action.id].financialData.categoryNames
                  : _.keys(action.data.financials),
              items: {
                ...state.details.entities[action.id].financialData.items,
                [action.databaseId + action.currencyId + +action.consolidated]: action.data,
              },
            },
          },
        },
        state.details
      ),
    };
  }),

  on(DrillDownActions.industryDescriptionsLoaded, (state, action) => {
    return {
      ...state,
      details: adapter.updateOne(
        {
          id: action.id,
          changes: {
            ...state.details[action.id],
            industryDescriptions: {
              ...state.details.entities[action.id].industryDescriptions,
              [action.databaseId]: action.data,
            },
          },
        },
        state.details
      ),
    };
  }),

  on(DrillDownActions.companyInfoLoaded, (state, action) => {
    return {
      ...state,
      details: adapter.updateOne(
        {
          id: action.id,
          changes: {
            ...state.details[action.id],
            companyInfo: {
              ...state.details.entities[action.id].companyInfo,
              [action.databaseId]: action.data,
            },
          },
        },
        state.details
      ),
    };
  }),

  on(DrillDownActions.businessDescriptionLoaded, (state, action) => {
    return {
      ...state,
      details: adapter.updateOne(
        {
          id: action.id,
          changes: {
            ...state.details[action.id],
            businessDescriptions: {
              ...state.details.entities[action.id].businessDescriptions,
              [action.databaseId]: action.data,
            },
          },
        },
        state.details
      ),
    };
  }),

  on(CompanySearchActions.removeNonStudyComparables, (state) => {
    return {
      ...state,
      details: adapter.removeMany((c) => !c.studyComparable, state.details),
    };
  }),

  on(StudyActions.clearCachedComparables, (state) => {
    return {
      ...state,
      details: adapter.getInitialState(),
    };
  })
);

export const { selectEntities } = adapter.getSelectors();

function addOrUpdateComparable(c: any, state: ComparablesState) {
  const exists = state.details.entities[c.comparableId] !== undefined;

  let comparable: Comparable;
  if (exists) {
    comparable = { ...state.details.entities[c.comparableId] };
  } else {
    comparable = new Comparable();
    comparable.companyInfo = {};
    comparable.companyInfo[c.databaseId] = new CompanyInfo();
  }

  comparable.comparableId = c.comparableId;
  comparable.companyName = c.companyName;
  comparable.databaseId = c.databaseId;
  comparable.companyInfo[c.databaseId].idNumber = c.bvdId;

  return comparable;
}
